/**
 * ZAgentFolders - Zod Validation Schemas for Agent Folders API
 *
 * Endpoints: /api/agents/folders/*
 * Controller: agents/folders/infrastructure/controllers/agent-folders.controller.ts
 *
 * UpdateIcon is multipart/form-data (Body typed as FormData for the generated
 * client; the backend reads the file via FileInterceptor).
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// SHARED SCHEMAS
// ═══════════════════════════════════════════════════════════════

const AgentFolderSchema = z.object({
  id: z.string(),
  name: z.string(),
  iconUrl: z.string().nullable(),
  parentId: z.string().nullable(),
  createdAt: z.string(), // ISO string
  updatedAt: z.string().nullable()
});

export type AgentFolder = z.infer<typeof AgentFolderSchema>;

const AgentFolderAssignmentSchema = z.object({
  agentId: z.string(),
  folderId: z.string()
});

export type AgentFolderAssignment = z.infer<typeof AgentFolderAssignmentSchema>;

const AgentFolderPathVarsSchema = z.object({
  id: z.string()
});

const AgentFolderGetAllResponseSchema = z.object({
  folders: z.array(AgentFolderSchema),
  assignments: z.array(AgentFolderAssignmentSchema)
});

export type AgentFolderGetAllResponse = z.infer<typeof AgentFolderGetAllResponseSchema>;

const AgentFolderCreateBodySchema = z.object({
  name: z.string().trim().min(1, "Folder name is required").max(255),
  parentId: z.string().optional()
});

const AgentFolderUpdateBodySchema = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  parentId: z.string().nullable().optional()
});

const AgentFolderAssignItemBodySchema = z.object({
  // Agent.id is a uuid column; the assignment table storing it is a bare String
  // with no foreign key, so an unvalidated id persists pointing at nothing. The
  // folderId sibling DOES have a foreign key, so a bad value there surfaces as a
  // Prisma error — a 500 where a 400 belongs. Only the body is tightened; the
  // response schema still describes rows written before this check existed.
  agentId: z.string().uuid(),
  folderId: z.string().uuid().nullable()
});

// ═══════════════════════════════════════════════════════════════
// ZAGENT FOLDERS MANIFEST
// ═══════════════════════════════════════════════════════════════

export const ZAgentFolders = {
  /**
   * GET /api/agents/folders
   * Returns all folders and agent assignments for the organization.
   */
  GetAll: {
    method: "GET" as const,
    path: "/api/agents/folders",
    Response: ApiSuccessSchema(AgentFolderGetAllResponseSchema)
  },

  /**
   * POST /api/agents/folders
   * Create a new agent folder.
   */
  Create: {
    method: "POST" as const,
    path: "/api/agents/folders",
    Body: AgentFolderCreateBodySchema,
    Response: ApiSuccessSchema(AgentFolderSchema)
  },

  /**
   * PATCH /api/agents/folders/:id
   * Rename or move a folder (update parentId).
   */
  Update: {
    method: "PATCH" as const,
    path: "/api/agents/folders/:id",
    PathVars: AgentFolderPathVarsSchema,
    Body: AgentFolderUpdateBodySchema,
    Response: ApiSuccessSchema(AgentFolderSchema)
  },

  /**
   * DELETE /api/agents/folders/:id
   * Delete a folder. Cascades: moves child folders & assignments to parent.
   */
  Delete: {
    method: "DELETE" as const,
    path: "/api/agents/folders/:id",
    PathVars: AgentFolderPathVarsSchema,
    Response: ApiSuccessSchema(z.object({ success: z.literal(true) }))
  },

  /**
   * POST /api/agents/folders/assign-item
   * Assign an agent to a folder, or move it to root (folderId = null).
   * Static route — MUST be registered before :id routes in the controller.
   */
  AssignItem: {
    method: "POST" as const,
    path: "/api/agents/folders/assign-item",
    Body: AgentFolderAssignItemBodySchema,
    Response: ApiSuccessSchema(z.record(z.string(), z.unknown()))
  },

  /**
   * POST /api/agents/folders/:id/icon
   * Upload a new icon image for a folder (multipart, "file" field).
   */
  UpdateIcon: {
    method: "POST" as const,
    path: "/api/agents/folders/:id/icon",
    PathVars: AgentFolderPathVarsSchema,
    Body: z.custom<FormData>(),
    Response: ApiSuccessSchema(z.object({ iconUrl: z.string() }))
  },

  /**
   * POST /api/agents/folders/:id/remove-icon
   * Remove the icon from a folder. JSON (no body needed).
   */
  RemoveIcon: {
    method: "POST" as const,
    path: "/api/agents/folders/:id/remove-icon",
    PathVars: AgentFolderPathVarsSchema,
    Response: ApiSuccessSchema(z.object({ iconUrl: z.null() }))
  }
} as const;
